import { type ReactElement, useState, useMemo } from 'react';
import classnames from 'classnames/bind';
import { Typography, SearchField, Spinner } from '@weareopenr/component-library';
import { type IPriceScheme } from 'src/types/price.types';
import styles from '../DesignPlatformExportFlow.module.scss';

const cx = classnames.bind(styles);

interface SelectPriceSchemeStepProps {
  priceSchemes: IPriceScheme[];
  priceSchemesLoading: boolean;
  selectedPriceSchemeId: string | null;
  onSelectPriceScheme: (priceSchemeId: string) => void;
}

const formatModificationDate = (priceScheme: IPriceScheme): string => {
  try {
    return priceScheme.modificationDateTime.format('DD/MM/YYYY HH:mm');
  } catch {
    return 'N/A';
  }
};

export const SelectPriceSchemeStep = ({
  priceSchemes,
  priceSchemesLoading,
  selectedPriceSchemeId,
  onSelectPriceScheme,
}: SelectPriceSchemeStepProps): ReactElement => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPriceSchemes = useMemo(() => {
    if (!searchQuery.trim()) {
      return priceSchemes;
    }
    const query = searchQuery.toLowerCase();
    return priceSchemes.filter((scheme) => scheme.name.toLowerCase().includes(query));
  }, [priceSchemes, searchQuery]);

  if (priceSchemesLoading) {
    return (
      <div className={styles['step-content']}>
        <div className={styles['loading-container']}>
          <Spinner size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles['step-content']}>
      <div className={styles['search-container']}>
        <SearchField
          isDisabled={false}
          placeholder="Search for a price scheme"
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className={styles['price-scheme-list']}>
        {filteredPriceSchemes.map((priceScheme) => {
          const isSelected = selectedPriceSchemeId === priceScheme.id;
          return (
            <button
              key={priceScheme.id}
              type="button"
              className={cx('price-scheme-item', { 'price-scheme-item-selected': isSelected })}
              onClick={() => onSelectPriceScheme(priceScheme.id)}
            >
              <div className={styles['price-scheme-radio']}>
                <div className={cx('radio-outer', { 'radio-outer-selected': isSelected })}>
                  {isSelected && <div className={styles['radio-inner']} />}
                </div>
              </div>
              <div className={styles['price-scheme-info']}>
                <Typography size="medium" weight="bold">
                  {priceScheme.name}
                </Typography>
                <Typography size="xsmall" className={styles['price-scheme-date']}>
                  Last edited - {formatModificationDate(priceScheme)}
                </Typography>
              </div>
            </button>
          );
        })}
        {filteredPriceSchemes.length === 0 && !priceSchemesLoading && (
          <Typography size="medium" className={styles['no-results']}>
            No price schemes found
          </Typography>
        )}
      </div>
    </div>
  );
};
